import React, { useEffect, useMemo, useState } from 'react'
import { browserHistory } from 'utils/navigation'

import '../stylesheets/caregiversassignment.scss'
import { AutocompleteSearch, Button } from 'components/UIElements'
import CaregiverAssignmentList from './CaregiverAssignmentList'
import { getStudyVersionID } from '../../../../../../../../../../utils/studyConfigUtils'

const CaregiverAssignmentPage = props => {
  const { caregiversList, participants, linkPptsAndCaregivers, study, location } = props

  const [selectedParticipant, setSelectedParticipant] = useState({})
  const [selectedCaregivers, setSelectedCaregivers] = useState([])
  const [isManuallyUpdated, setIsManuallyUpdated] = useState(false)

  const isCompleteAssignmentBtnDisabled = !isManuallyUpdated

  const currentStudy = study?.currentStudy
  const studyVersionID = getStudyVersionID(location)

  const shouldRenderCaregiverManagement = selectedParticipant?.key

  const allParticipantsRaw = useMemo(() => {
    return Object.values(participants?.participants || {})
  }, [participants?.participants])

  const participantOptions = useMemo(() => {
    return allParticipantsRaw
      .filter(p => p.some(item => item.key === 'subjectId' && item.value !== '---'))
      .map(p => ({
        key: p.find(item => item.key === 'id')?.value || '',
        text: p.find(item => item.key === 'subjectId')?.value || '',
      }))
  }, [allParticipantsRaw])

  const validCaregivers = useMemo(() => {
    return caregiversList.filter(caregiverData =>
      caregiverData.some(item => item.key === 'subjectId' && item.value !== '---'),
    )
  }, [caregiversList])

  const caregiversWithUpdatedRelationship = useMemo(() => {
    return validCaregivers.map(group => {
      const newGroup = group.map(item => ({ ...item }))

      const idItem = newGroup.find(item => item.key === 'id')
      const relationItem = newGroup.find(item => item.key === 'relationToParticipant')

      if (idItem && relationItem) {
        const match = selectedCaregivers.find(f => f.key === idItem.value)
        if (match) {
          relationItem.value = match.relationship
        }
      }

      return newGroup
    })
  }, [validCaregivers, selectedCaregivers])

  const getSelectedPtp = ptpList =>
    ptpList.find(p => p.find(item => item.key === 'id' && item.value === selectedParticipant?.key))

  useEffect(() => {
    if (!selectedParticipant?.key || !Array.isArray(allParticipantsRaw)) {
      setSelectedCaregivers([])
      return
    }

    const participant = getSelectedPtp(allParticipantsRaw)

    if (!participant) {
      setSelectedCaregivers([])
      return
    }

    const linkedCaregivers = participant.find(item => item.key === 'linkedCaregivers')?.value || []

    const mappedCaregivers = linkedCaregivers
      .map(entry => {
        const exists = validCaregivers.find(group =>
          group.find(item => item.key === 'id' && item.value === entry.caregiver_id),
        )
        return exists ? { key: entry.caregiver_id, relationship: entry.relationship } : null
      })
      .filter(Boolean)

    setSelectedCaregivers(mappedCaregivers)
  }, [selectedParticipant?.key, validCaregivers, allParticipantsRaw])

  const updateCaregiverRelation = (caregiverId, newRelation) => {
    setIsManuallyUpdated(true)

    setSelectedCaregivers(prev =>
      prev.map(item => (item.key === caregiverId ? { ...item, relationship: newRelation?.text || 'Other' } : item)),
    )
  }

  const toggleFilteredItem = item => {
    if (!item) return

    setIsManuallyUpdated(true)

    if (Array.isArray(item)) {
      setSelectedCaregivers([])
      return
    }

    setSelectedCaregivers(prev => {
      const exists = prev.find(i => i.key === item.key)
      return exists ? prev.filter(i => i.key !== item.key) : [...prev, item]
    })
  }

  const handleCompleteAssignment = () => {
    const formattedCaregivers = selectedCaregivers.map(({ key, ...rest }) => ({ id: key, ...rest }))
    linkPptsAndCaregivers({
      studyID: currentStudy.id,
      isParticipantType: true,
      id: selectedParticipant?.key,
      selectedList: formattedCaregivers,
    })
  }

  return (
    <main className='caregiver-assignment'>
      <p>Note: Caregivers and participants must first be created before they can be assigned.</p>
      <p>Assign using</p>
      <div className='participant-caregiver-selection'>
        <p style={{ marginTop: '10px' }}>Participant's Subject ID</p>
        <AutocompleteSearch
          id='search-participants'
          checked={selectedParticipant}
          shouldReturnItem={true}
          toggleItem={setSelectedParticipant}
          placeholder='Select a Participant'
          isDropdown={true}
          hideCheckbox={true}
          initialValue={selectedParticipant?.text || ''}
          list={participantOptions || []}
        />
      </div>
      {shouldRenderCaregiverManagement && (
        <div>
          <CaregiverAssignmentList
            {...props}
            study={currentStudy}
            setFilteredItems={toggleFilteredItem}
            filteredItems={selectedCaregivers}
            updateCaregiverRelation={updateCaregiverRelation}
            caregivers={caregiversWithUpdatedRelationship}
            selectedParticipantSubjectId={selectedParticipant?.text}
            selectedPtp={getSelectedPtp(allParticipantsRaw)}
          />
          <div className='button-list'>
            <Button
              grey
              id='cancel'
              content='Cancel'
              onClick={() => browserHistory.push(`/studies/${currentStudy.id}/${studyVersionID}/participants`)}
            />
            <Button
              id='assign-ppt-caregiver'
              disabled={isCompleteAssignmentBtnDisabled}
              content={'Complete Assignment'}
              onClick={handleCompleteAssignment}
            />
          </div>
        </div>
      )}
    </main>
  )
}

export default CaregiverAssignmentPage
